"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, type ReactNode } from "react";
import { type TenantConfig, getDefaultTenant } from "@/lib/tenant/config";

/**
 * Tenant context type
 */
interface TenantContextType {
  tenant: TenantConfig;
}

/**
 * Create context with default tenant
 */
const TenantContext = createContext<TenantContextType>({
  tenant: getDefaultTenant()
});

/**
 * Props for TenantProvider
 */
interface TenantProviderProps {
  tenant: TenantConfig;
  children: ReactNode;
}

/**
 * Provider component that makes tenant config available to all children
 */
export function TenantProvider(t0) {
  const $ = _c(5);
  const {
    tenant,
    children
  } = t0;
  let t1;
  if ($[0] !== tenant) {
    t1 = {
      tenant
    };
    $[0] = tenant;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <TenantContext.Provider value={t1}>{children}</TenantContext.Provider>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Hook to access tenant config
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context.tenant;
}

/**
 * Hook to check if current tenant supports a specific offering type
 */
export function useTenantOfferings() {
  const $ = _c(6);
  const tenant = useTenant();
  let t0;
  if ($[0] !== tenant.offeringTypes) {
    t0 = type => tenant.offeringTypes.includes(type as TenantConfig["offeringTypes"][number]);
    $[0] = tenant.offeringTypes;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0 || $[3] !== tenant.features.showAllOfferings || $[4] !== tenant.offeringTypes) {
    t1 = {
      offerings: tenant.offeringTypes,
      hasOffering: t0,
      showAllOfferings: tenant.features.showAllOfferings
    };
    $[2] = t0;
    $[3] = tenant.features.showAllOfferings;
    $[4] = tenant.offeringTypes;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/**
 * Hook to get tenant theme
 */
export function useTenantTheme() {
  const tenant = useTenant();
  return tenant.theme;
}

/**
 * Hook to get tenant features
 */
export function useTenantFeatures() {
  const tenant = useTenant();
  return tenant.features;
}